import { z } from "zod";

/**
 * Formulário de cadastro de parceiros (/parceiro/cadastro).
 * Captura os dados pessoais + dados da PJ que preenchem o contrato de parceria.
 * 3 steps: consentimento → dados pessoais → dados da PJ.
 */

// 27 UFs — value = sigla (vai pro contrato), label = nome
export const UF_OPTIONS = [
  { value: "AC", label: "Acre" },
  { value: "AL", label: "Alagoas" },
  { value: "AP", label: "Amapá" },
  { value: "AM", label: "Amazonas" },
  { value: "BA", label: "Bahia" },
  { value: "CE", label: "Ceará" },
  { value: "DF", label: "Distrito Federal" },
  { value: "ES", label: "Espírito Santo" },
  { value: "GO", label: "Goiás" },
  { value: "MA", label: "Maranhão" },
  { value: "MT", label: "Mato Grosso" },
  { value: "MS", label: "Mato Grosso do Sul" },
  { value: "MG", label: "Minas Gerais" },
  { value: "PA", label: "Pará" },
  { value: "PB", label: "Paraíba" },
  { value: "PR", label: "Paraná" },
  { value: "PE", label: "Pernambuco" },
  { value: "PI", label: "Piauí" },
  { value: "RJ", label: "Rio de Janeiro" },
  { value: "RN", label: "Rio Grande do Norte" },
  { value: "RS", label: "Rio Grande do Sul" },
  { value: "RO", label: "Rondônia" },
  { value: "RR", label: "Roraima" },
  { value: "SC", label: "Santa Catarina" },
  { value: "SP", label: "São Paulo" },
  { value: "SE", label: "Sergipe" },
  { value: "TO", label: "Tocantins" },
] as const;

// ---------- Formatters (máscaras) ----------

export { formatPhone } from "@/lib/phone";

export function formatCNPJ(value: string): string {
  const d = value.replace(/\D/g, "").slice(0, 14);
  let out = d;
  if (d.length > 2) out = `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length > 5) out = `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length > 8)
    out = `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  if (d.length > 12)
    out = `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(
      8,
      12
    )}-${d.slice(12)}`;
  return out;
}

export function formatCEP(value: string): string {
  const d = value.replace(/\D/g, "").slice(0, 8);
  if (d.length > 5) return `${d.slice(0, 5)}-${d.slice(5)}`;
  return d;
}

// ---------- Schemas por step ----------

export const step1Schema = z.object({
  autoriza_contato: z.literal(true, {
    errorMap: () => ({
      message: "É preciso confirmar o interesse para continuar",
    }),
  }),
});

export const step2Schema = z.object({
  fullname: z
    .string()
    .min(3, "Informe seu nome completo")
    .refine((v) => v.trim().includes(" "), {
      message: "Informe nome e sobrenome",
    }),
  email: z.string().email("E-mail inválido"),
  phone: z.string().min(14, "Telefone com DDD: (XX) XXXXX-XXXX"),
});

export const step3Schema = z.object({
  pj_razao_social: z.string().min(2, "Informe a razão social"),
  pj_cnpj: z
    .string()
    .refine((v) => v.replace(/\D/g, "").length === 14, {
      message: "CNPJ deve ter 14 dígitos",
    }),
  pj_cep: z
    .string()
    .refine((v) => v.replace(/\D/g, "").length === 8, {
      message: "CEP deve ter 8 dígitos",
    }),
  pj_logradouro: z.string().min(2, "Informe o logradouro"),
  pj_numero: z.string().min(1, "Informe o número"),
  pj_complemento: z.string().optional(),
  pj_bairro: z.string().min(2, "Informe o bairro"),
  pj_cidade: z.string().min(2, "Informe a cidade"),
  pj_uf: z.string().min(2, "Selecione a UF"),
  pj_email: z.string().email("E-mail da empresa inválido"),
});

export const cadastroFormSchema = step1Schema
  .merge(step2Schema)
  .merge(step3Schema);

export type CadastroFormData = z.infer<typeof cadastroFormSchema>;
